import type {
  ConsultationContactMode,
  ConsultationStatus,
  ConsultationTopic,
  IncidentCategory,
  IncidentStatus,
} from "./api";
import { canManageConsultations } from "./permissions";

/**
 * ป้ายของ ແຈ້ງເຫດ / ນັດປຶກສາ (ADR-0027) — คำชุดเดียวกับแอป (apps/web-mobile/src/lib/{incidents,consultations}.ts)
 * ให้เจ้าหน้าที่กับประชาชนเห็นคำเดียวกัน · สิทธิ์และเส้นทางสถานะจริงตัดสินที่ server (`next_statuses`/`can_manage`)
 */

/** ผู้ที่อ่าน/จัดการนัดปรึกษาได้ = สิทธิ์ SUP-01 (ADR-0030) — ใช้คุมเมนู/จอ ไม่ใช่กลไกความปลอดภัย */
export function canReviewConsultations(): boolean {
  return canManageConsultations();
}

export const INCIDENT_CATEGORY_LABEL: Record<IncidentCategory, string> = {
  emergency: "ເຫດດ່ວນ",
  dispute: "ຂໍ້ຂັດແຍ່ງ",
  utility: "ໄຟຟ້າ, ນ້ຳປະປາ, ຖະໜົນ",
  other: "ເລື່ອງອື່ນໆ",
};
export const INCIDENT_CATEGORY_ORDER: IncidentCategory[] = ["emergency", "dispute", "utility", "other"];

export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  new: "ລໍຖ້າຮັບເລື່ອງ",
  acknowledged: "ຮັບເລື່ອງແລ້ວ",
  in_progress: "ກຳລັງດຳເນີນການ",
  resolved: "ແກ້ໄຂແລ້ວ",
  rejected: "ບໍ່ຮັບເລື່ອງ",
};
/** ป้ายบนปุ่มเปลี่ยนสถานะ (คำกริยา) */
export const INCIDENT_ACTION_LABEL: Record<IncidentStatus, string> = {
  new: "ລໍຖ້າຮັບເລື່ອງ",
  acknowledged: "ຮັບເລື່ອງ",
  in_progress: "ເລີ່ມດຳເນີນການ",
  resolved: "ແກ້ໄຂແລ້ວ",
  rejected: "ບໍ່ຮັບເລື່ອງ",
};
export const INCIDENT_STATUS_ORDER: IncidentStatus[] = ["new", "acknowledged", "in_progress", "resolved", "rejected"];

export function incidentStatusBadge(s: IncidentStatus): string {
  switch (s) {
    case "new":
      return "status-badge status-badge--pending";
    case "acknowledged":
    case "in_progress":
      return "status-badge status-badge--info";
    case "resolved":
      return "status-badge status-badge--success";
    case "rejected":
      return "status-badge status-badge--danger";
  }
}

export const CONSULT_TOPIC_LABEL: Record<ConsultationTopic, string> = {
  mediation: "ໄກ່ເກ່ຍຂໍ້ຂັດແຍ່ງ",
  inheritance: "ແບ່ງມູນມໍລະດົກ",
  divorce: "ຢ່າຮ້າງ",
  other: "ອື່ນໆ",
};
export const CONSULT_TOPIC_ORDER: ConsultationTopic[] = ["mediation", "inheritance", "divorce", "other"];

export const CONTACT_MODE_LABEL: Record<ConsultationContactMode, string> = {
  in_person: "ພົບທີ່ຫ້ອງການ",
  phone: "ໂທລະສັບ",
  video: "ວິດີໂອ",
};

export const CONSULT_STATUS_LABEL: Record<ConsultationStatus, string> = {
  requested: "ລໍຖ້ານັດ",
  scheduled: "ນັດແລ້ວ",
  completed: "ປຶກສາແລ້ວ",
  declined: "ບໍ່ຮັບນັດ",
  cancelled: "ຍົກເລີກແລ້ວ",
};
export const CONSULT_STATUS_ORDER: ConsultationStatus[] = ["requested", "scheduled", "completed", "declined", "cancelled"];

export function consultStatusBadge(s: ConsultationStatus): string {
  switch (s) {
    case "requested":
      return "status-badge status-badge--pending";
    case "scheduled":
      return "status-badge status-badge--info";
    case "completed":
      return "status-badge status-badge--success";
    case "declined":
    case "cancelled":
      return "status-badge status-badge--danger";
  }
}

/** ชื่อช่องสถานที่ตามช่องทาง — วิดีโอคือลิงก์ */
export function meetingPlaceLabel(mode: ConsultationContactMode): string {
  return mode === "video" ? "ລິ້ງວິດີໂອ" : mode === "phone" ? "ໝາຍເຫດການໂທ" : "ສະຖານທີ່ນັດ";
}

/** สถานที่บังคับเมื่อพบที่ห้องการ/วิดีโอ (กฎเดียวกับ `Consultation.Schedule` ฝั่ง Go) */
export function meetingPlaceRequired(mode: ConsultationContactMode): boolean {
  return mode !== "phone";
}

/** ลิงก์ที่เปิดได้ (http/https) — สถานที่ธรรมดาไม่ใช่ลิงก์ */
export function isHttpLink(s: string): boolean {
  return /^https?:\/\/\S+$/i.test(s.trim());
}

/** ที่ตั้งสำหรับแสดง — ไม่มีชื่อบ้าน = ระบุบ้านไม่ได้ (ต้องโผล่ ไม่ใช่หาย) */
export function placeLabel(p: { village_name_lo: string; district_name_lo: string }): string {
  if (!p.village_name_lo) return "ບໍ່ລະບຸບ້ານ";
  return p.district_name_lo ? `${p.village_name_lo}, ${p.district_name_lo}` : p.village_name_lo;
}

/** ลิงก์ Google Maps ของพิกัด */
export function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${String(lat)},${String(lng)}`;
}

/** พิกัด → `17.96670, 102.60000 · ±15 ມ` */
export function formatCoords(lat: number, lng: number, accuracyM: number | null): string {
  const base = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  return accuracyM === null ? base : `${base} · ±${String(Math.round(accuracyM))} ມ`;
}

const DATE_TIME_FMT = new Intl.DateTimeFormat("lo-LA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "Asia/Vientiane",
});

/** วันเวลาตามเวลาลาว (นัด/เวลาแจ้ง) */
export function dateTimeLo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_TIME_FMT.format(d);
}

/**
 * ค่า `<input type="datetime-local">` (เวลาในเครื่องของเบราว์เซอร์) → ISO-8601 พร้อมโซน — ว่าง/ผิด = null
 * เจ้าหน้าที่อยู่ในลาว เครื่องจึงเป็นเวลาลาวอยู่แล้ว
 */
export function localInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** ISO → ค่าเริ่มต้นของ `<input type="datetime-local">` (เวลาในเครื่อง) */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
